import cloneDeep from 'lodash/cloneDeep'
import type { EditablePoint, IPoint } from '@/modules/Editor/point'
import type { AnnotationData } from '@/modules/Editor/AnnotationData'
import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import type { SharedToolState } from '@/modules/Editor/managers/toolManager'
import { euclideanDistance } from '@/modules/Editor/algebra'
import { MultiSelectionRectangle } from './multiSelectionRectangle'
import type { View } from '@/modules/Editor/views/view'

/**
 * Tool state using discriminated unions to ensure only valid states are possible
 * Optimized to store only IDs and initial data to reduce memory consumption
 */
type ToolState =
  | { type: 'IDLE' }
  | {
      type: 'MOVING_ANNOTATIONS'
      movingAnnotationIds: string[]
      // Store only initial data for undo - get current annotations from annotation manager
      annotationsInitialData: AnnotationData[]
    }
  | {
      type: 'MOVING_VERTEX'
      vertex: EditablePoint
      // Keep annotation tracking when moving vertex
      movingAnnotationIds: string[]
      annotationsInitialData: AnnotationData[]
    }

/**
 * Complete state and data management for EditTool
 *
 * This is the single source of truth for all EditTool state, including:
 * - Mouse position tracking
 * - Tool interaction state (idle, moving annotations, moving vertex)
 * - Rectangle selection state
 * - Panning state
 * - Target item tracking
 *
 * Uses discriminated unions to ensure only valid state combinations are possible.
 */
export class EditToolState {
  /**
   * Tool state using discriminated unions to prevent invalid combinations
   */
  private state: ToolState = { type: 'IDLE' }

  /**
   * Mouse position tracking (shared across all states)
   */
  previousMouseMovePosition?: IPoint
  initialMouseDownPosition?: IPoint

  /**
   * What's currently targeted by the mouse (shared across states)
   */
  targetedItemId: string | undefined = undefined
  targetedItemsIndex: number | undefined = undefined

  /**
   * Shared state for panning coordination with other tools
   */
  sharedState: SharedToolState = {
    isPanning: false,
  }

  /**
   * Rectangle selection manager
   */
  private rectangleSelection = new MultiSelectionRectangle()

  // ==================== MOUSE POSITION TRACKING ====================

  updateMousePosition(position: IPoint): void {
    this.previousMouseMovePosition = position
  }

  setInitialMousePosition(position: IPoint): void {
    this.initialMouseDownPosition = position
    this.previousMouseMovePosition = position
  }

  getCurrentDistanceMoved(): number {
    const { previousMouseMovePosition, initialMouseDownPosition } = this
    if (!initialMouseDownPosition || !previousMouseMovePosition) {
      return 0
    }
    return euclideanDistance(initialMouseDownPosition, previousMouseMovePosition)
  }

  // ==================== STATE QUERIES ====================

  getCurrentState(): 'IDLE' | 'MOVING_ANNOTATIONS' | 'MOVING_VERTEX' {
    return this.state.type
  }

  isIdle(): boolean {
    return this.state.type === 'IDLE'
  }

  isMovingAnnotations(): boolean {
    return this.state.type === 'MOVING_ANNOTATIONS'
  }

  isMovingVertex(): boolean {
    return this.state.type === 'MOVING_VERTEX'
  }

  isMoving(): boolean {
    return this.isMovingAnnotations() || this.isMovingVertex()
  }

  didMove(): boolean {
    return this.isMoving() && this.getCurrentDistanceMoved() > 0
  }

  // ==================== STATE TRANSITIONS ====================

  startMovingAnnotations(annotationIds: string[]): void {
    this.state = {
      type: 'MOVING_ANNOTATIONS',
      movingAnnotationIds: [...annotationIds],
      annotationsInitialData: [],
    }
  }

  /**
   * Start tracking annotations for movement with undo data
   * Optimized to store only IDs and initial data, reducing memory usage
   */
  startMovingAnnotationsWithData(annotations: Annotation[]): void {
    this.state = {
      type: 'MOVING_ANNOTATIONS',
      movingAnnotationIds: annotations.map((a) => a.id),
      annotationsInitialData: annotations.map((annotation) => cloneDeep(annotation.data)),
    }
  }

  startMovingVertex(vertex: EditablePoint): void {
    // Preserve annotation tracking from MOVING_ANNOTATIONS state when transitioning to vertex movement
    if (this.state.type === 'MOVING_ANNOTATIONS') {
      this.state = {
        type: 'MOVING_VERTEX',
        vertex,
        movingAnnotationIds: this.state.movingAnnotationIds,
        annotationsInitialData: this.state.annotationsInitialData,
      }
    } else {
      // Fallback for direct vertex movement (shouldn't happen in normal flow)
      this.state = {
        type: 'MOVING_VERTEX',
        vertex,
        movingAnnotationIds: [],
        annotationsInitialData: [],
      }
    }
  }

  // ==================== ANNOTATION MOVEMENT DATA ====================

  getMovingAnnotationIds(): string[] {
    if (this.isIdle()) {
      return []
    }
    const state = this.state as Extract<ToolState, { movingAnnotationIds: string[] }>
    return [...state.movingAnnotationIds]
  }

  /**
   * Get annotations currently being moved with their initial data
   * Note: This method now requires annotation access from external source
   * Consider using getMovingAnnotationIds() and accessing annotations externally for better performance
   */
  getMovingAnnotations(getAnnotationById: (id: string) => Annotation | undefined): Array<{
    annotation: Annotation
    initialData: AnnotationData
  }> {
    if (this.isIdle()) {
      return []
    }

    const state = this.state as Extract<ToolState, { movingAnnotationIds: string[] }>

    return state.movingAnnotationIds.reduce<
      Array<{ annotation: Annotation; initialData: AnnotationData }>
    >((acc, id, index) => {
      const annotation = getAnnotationById(id)
      const initialData = state.annotationsInitialData[index]
      if (annotation && initialData) {
        acc.push({ annotation, initialData })
      }
      return acc
    }, [])
  }

  /**
   * Get just the annotations (without undo data)
   * Note: This method now requires annotation access from external source
   */
  getMovingAnnotationObjects(
    getAnnotationById: (id: string) => Annotation | undefined,
  ): Annotation[] {
    if (this.isIdle()) {
      return []
    }

    const state = this.state as Extract<ToolState, { movingAnnotationIds: string[] }>

    return state.movingAnnotationIds.reduce<Annotation[]>((acc, id) => {
      const annotation = getAnnotationById(id)
      if (annotation) {
        acc.push(annotation)
      }
      return acc
    }, [])
  }

  /**
   * Get just the initial data for undo operations
   */
  getMovingAnnotationsInitialData(): AnnotationData[] {
    if (this.isIdle()) {
      return []
    }

    const state = this.state as Extract<ToolState, { annotationsInitialData: AnnotationData[] }>
    return [...state.annotationsInitialData]
  }

  /**
   * Get current data for undo operations
   * Note: This method now requires annotation access from external source
   */
  getMovingAnnotationsCurrentData(
    getAnnotationById: (id: string) => Annotation | undefined,
  ): AnnotationData[] {
    if (this.isIdle()) {
      return []
    }

    const state = this.state as Extract<ToolState, { movingAnnotationIds: string[] }>

    return state.movingAnnotationIds.reduce<AnnotationData[]>((acc, id) => {
      const annotation = getAnnotationById(id)
      if (annotation) {
        acc.push(annotation.data)
      }
      return acc
    }, [])
  }

  /**
   * Check if any annotations are being tracked for movement
   */
  hasMovingAnnotations(): boolean {
    if (this.isIdle()) {
      return false
    }

    const state = this.state as Extract<ToolState, { annotationsInitialData: AnnotationData[] }>
    return state.annotationsInitialData.length > 0
  }

  // ==================== VERTEX MOVEMENT ====================

  getMovingVertex(): EditablePoint | undefined {
    if (this.state.type !== 'MOVING_VERTEX') {
      return undefined
    }
    return this.state.vertex
  }

  get vertexMoving(): Readonly<EditablePoint | undefined> {
    return this.getMovingVertex()
  }

  // ==================== TARGET ITEM MANAGEMENT ====================

  setTargetedItem(itemId: string | undefined, itemIndex?: number): void {
    this.targetedItemId = itemId
    this.targetedItemsIndex = itemIndex
  }

  clearTargetedItem(): void {
    this.targetedItemId = undefined
    this.targetedItemsIndex = undefined
  }

  // ==================== RECTANGLE SELECTION ====================

  get isDrawingRectangle(): boolean {
    return this.rectangleSelection?.isDrawing ?? false
  }

  startRectangleSelection(canvasPoint: IPoint, currentSelection: string[]): void {
    this.rectangleSelection.startSelection(canvasPoint, currentSelection)
  }

  updateRectangleSelection(canvasPoint: IPoint, view: View): void {
    this.rectangleSelection.update(canvasPoint, view)
  }

  completeRectangleSelection(): void {
    this.rectangleSelection.complete()
  }

  clearRectangleDrawing(view: View): void {
    this.rectangleSelection.clearDrawing(view)
  }

  resetRectangleSelection(): void {
    this.rectangleSelection.reset()
  }

  // ==================== RESET ====================

  reset(): void {
    this.state = { type: 'IDLE' }
    this.previousMouseMovePosition = undefined
    this.initialMouseDownPosition = undefined
    this.clearTargetedItem()
    this.resetRectangleSelection()
  }
}
